import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ArrowLeft } from 'lucide-react';
import SalesForm from '../components/sales/SalesForm';
import { useData } from '../contexts/DataContext';

const EditTransactionPage = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { productsData, refreshData } = useData();

  const [transaction, setTransaction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTransaction = async () => {
      if (!transactionId) {
        setError('No transaction ID provided');
        setIsLoading(false);
        return;
      }
      try {
        const transactionRef = doc(db, 'Sales', transactionId);
        const snap = await getDoc(transactionRef);
        if (!snap.exists()) {
          setError('Transaction not found');
          setIsLoading(false);
          return;
        }
        const data = snap.data();
        setTransaction({
          transactionId: data.transactionId || transactionId,
          clientName: data.clientName || '',
          company: data.company || 'createv',
          productName: data.productName || '',
          productVariant: data.productVariant || '',
          price: data.price !== undefined ? String(data.price) : '',
          amount: data.amount !== undefined ? String(data.amount) : '1',
          emailEdu: data.emailEdu || '',
          clientEmail: data.clientEmail || '',
          paymentMethod: data.paymentMethod || 'N/A',
          contact1: data.contact1 || '',
          contact1Type: data.contact1Type || 'Whatsapp',
          contact2: data.contact2 || '',
          contact2Type: data.contact2Type || 'Whatsapp',
          date: data.date || new Date().toISOString().split('T')[0],
          day: data.day || '',
          status: data.status || 'Completed',
          subscriptionPeriod: data.subscriptionPeriod || '12 months',
          notes: data.notes || '',
        });
      } catch (err) {
        setError(`Failed to load transaction: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };
    loadTransaction();
  }, [transactionId]);

  const handleSuccess = () => {
    refreshData();
    navigate('/home/sales', {
      state: { message: 'Transaction updated successfully!', type: 'success' },
    });
  };

  const handleBack = () => navigate('/home/sales');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-gray-500">Loading transaction...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 gap-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={handleBack}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Back to Sales
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple top bar with back button */}
      <div className="bg-white shadow-sm border-b px-6 py-3 flex items-center gap-4">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Sales</span>
        </button>
        <span className="text-gray-400">|</span>
        <span className="text-gray-700 font-medium">Editing: {transactionId}</span>
      </div>

      {/* Use the exact same SalesForm as Add New Sale */}
      <SalesForm
        transaction={transaction}
        setTransaction={setTransaction}
        isEditing={true}
        editingId={transactionId}
        products={productsData || []}
        onSubmitSuccess={handleSuccess}
        generateTransactionId={() => transactionId}
        fetchSales={null}
      />
    </div>
  );
};

export default EditTransactionPage;

